import { Component, ErrorHandler, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitajanTiimi, KirjanpitajanTiimikokonaisuus } from 'app/_jaettu-lemonator/model/kirjanpitaja'

import { TiimienHallintaService } from '../tiimien-hallinta.service'

export interface LisaaTiimiKokonaisuuteenDialogData {
  kokonaisuusAvain: KirjanpitajanTiimikokonaisuus['avain']
  kokonaisuusNimi: string
  // Valittavissa olevat tiimit — ei sisällä jo aktiivisesti kokonaisuuteen kuuluvia
  valittavissaOlevatTiimit: { avain: KirjanpitajanTiimi['avain']; nimi: string }[]
}

interface TiimiKokonaisuuteenLomake {
  tiimiAvain: KirjanpitajanTiimi['avain'] | null
  alku: Date | null
  loppu: Date | null
}

@Component({
  templateUrl: './lisaa-tiimi-kokonaisuuteen.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LisaaTiimiKokonaisuuteenDialog {
  lomake: FieldTree<TiimiKokonaisuuteenLomake>

  virhe: string = null

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LisaaTiimiKokonaisuuteenDialogData,
    private _dialogRef: MatDialogRef<LisaaTiimiKokonaisuuteenDialog>,
    private _ladataanService: LadataanService,
    private _hallintaService: TiimienHallintaService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
  ) {
    this.lomake = form(signal<TiimiKokonaisuuteenLomake>({ tiimiAvain: null, alku: new Date(), loppu: null }), polku => {
      required(polku.tiimiAvain)
      required(polku.alku)
    })
  }

  peruuta() {
    this._dialogRef.close()
  }

  async tallenna() {
    if (!this.lomake().valid()) {
      return
    }
    this.virhe = null
    const arvot = this.lomake().value()
    if (!arvot.tiimiAvain) {
      this.virhe = 'Valitse tiimi.'
      return
    }
    if (!arvot.alku) {
      this.virhe = 'Anna alkupäivä.'
      return
    }
    const alku: NumberDate = this._dateService.dateToNumber(arvot.alku)
    const loppu: NumberDate | null = arvot.loppu ? this._dateService.dateToNumber(arvot.loppu) : null
    if (loppu !== null && loppu < alku) {
      this.virhe = 'Loppupäivä ei voi olla ennen alkupäivää.'
      return
    }
    try {
      this._ladataanService.aloitaLataaminen()
      await this._hallintaService.lisaaTiimiKokonaisuuteen(this.data.kokonaisuusAvain, { avain: arvot.tiimiAvain, alku, loppu })
      this._snackbar.open('Tiimi lisätty kokonaisuuteen', 'OK', { duration: 5000 })
      this._dialogRef.close(true)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Lisääminen epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
